// White Paper v1.0 — "Foundation & Protocol Overview"
// Content reflects only officially stated project information.

export const WHITEPAPER_V1 = {
  version: "1.0",
  title: "Foundation & Protocol Overview",
  sections: [
    {
      id: "introduction",
      title: "Introduction & Vision",
      paragraphs: [
        "Jovino Coin (JCV) is a fixed-supply, utility-focused digital asset designed to power a growing decentralized ecosystem. Built on the BNB Smart Chain using the BEP-20 standard, JCV is intended to become the native digital asset supporting the broader Jovino ecosystem.",
        "Jovino Coin is designed to support payments, staking, governance, ecosystem services, and future Web3 applications. The long-term vision is for JCV to serve as the native utility asset across payments, governance and decentralized applications.",
      ],
    },
    {
      id: "principles",
      title: "Core Principles",
      list: ["Transparency", "Security", "Sustainability", "Community", "Innovation", "Utility", "Long-term ecosystem development"],
    },
    {
      id: "specifications",
      title: "Token Specifications",
      table: [
        ["Name", "Jovino Coin"],
        ["Symbol", "JCV"],
        ["Blockchain", "BNB Smart Chain"],
        ["Standard", "BEP-20"],
        ["Decimals", "18"],
        ["Maximum Supply", "72,000,000,000,000 JCV"],
        ["Inflation", "None"],
        ["Minting", "Permanently Disabled"],
        ["Burning", "Community-approved buyback & burn"],
        ["Governance", "DAO — Future Phase"],
      ],
    },
    {
      id: "supply",
      title: "Fixed Supply & Scarcity",
      paragraphs: [
        "The total supply of Jovino Coin is permanently fixed at 72,000,000,000,000 JCV. Once deployed, no additional JCV can be created: minting is permanently disabled, there is no inflation, and the supply schedule is fully predictable.",
        "Fixed supply does not guarantee price appreciation. It guarantees that holders can never be diluted by new issuance.",
      ],
    },
    {
      id: "tokenomics",
      title: "Tokenomics & Allocations",
      paragraphs: ["The fixed maximum supply of 72,000,000,000,000 JCV is allocated as follows. Allocations total exactly 100%."],
      table: [
        ["Community & Public Distribution", "40% — 28,800,000,000,000 JCV"],
        ["Liquidity & Exchange Support", "20% — 14,400,000,000,000 JCV"],
        ["Ecosystem Development", "15% — 10,800,000,000,000 JCV"],
        ["Staking Rewards", "10% — 7,200,000,000,000 JCV"],
        ["Foundation Treasury", "10% — 7,200,000,000,000 JCV"],
        ["Team & Advisors", "5% — 3,600,000,000,000 JCV"],
        ["Total", "100% — 72,000,000,000,000 JCV"],
      ],
    },
    {
      id: "vesting",
      title: "Vesting & Release Schedules",
      table: [
        ["Team & Advisors", "Lock: 12 Months — Linear release over 36 Months"],
        ["Foundation Treasury", "Lock: 6 Months — Governance-controlled release"],
        ["Ecosystem Development", "Lock: 3 Months — Milestone-based release"],
        ["Community Allocation", "No lock — Public distribution schedule"],
        ["Liquidity", "Immediate — Controlled deployment"],
        ["Staking Rewards", "No lock — Distributed according to staking protocol"],
      ],
    },
    {
      id: "utility",
      title: "Utility",
      paragraphs: [
        "JCV is designed with utility that goes beyond the token: network payments, merchant payments through supported future integrations, staking, participation in future governance proposals, ecosystem services (premium features, APIs and decentralized services in future Jovino applications), and developer incentives through ecosystem grants denominated in JCV.",
        "Network payments are native to the token at launch. Merchant payments, staking, governance, ecosystem services and developer incentive programs are planned functionality or future integrations.",
      ],
    },
    {
      id: "staking",
      title: "Staking (Planned)",
      paragraphs: [
        "Holders may lock JCV and participate in the staking ecosystem. Rewards are intended to come from the dedicated staking allocation of 7,200,000,000,000 JCV rather than newly minted tokens.",
      ],
      table: [
        ["90 Days", "1.00× reward multiplier"],
        ["180 Days", "1.20× reward multiplier"],
        ["240 Days", "1.50× reward multiplier"],
        ["365 Days", "2.00× reward multiplier"],
      ],
    },
    {
      id: "ecosystem",
      title: "Ecosystem Components",
      paragraphs: ["The following components form the planned Jovino ecosystem, with JCV at its center:"],
      list: [
        "Jovino Coin Wallet — non-custodial wallet for storing, sending, receiving and staking JCV (planned)",
        "Jovino Coin Explorer — transactions, treasury transparency and governance activity (planned)",
        "Governance Portal — proposal and voting platform (future phase)",
        "Developer Hub — documentation, APIs, SDKs and examples (planned)",
        "Merchant Portal — tools for businesses to accept JCV payments and manage settlements (planned)",
      ],
    },
    {
      id: "buyback-burn",
      title: "Buyback & Burn",
      paragraphs: [
        "Jovino Coin is not inherently deflationary through transaction taxes. The ecosystem may periodically use revenue generated from ecosystem services to purchase JCV on the open market. Purchased tokens may then be transferred to an irrecoverable burn address, permanently reducing circulating supply.",
        "Buyback frequency and size are not guaranteed and are subject to transparent policies and community approval over time.",
      ],
    },
    {
      id: "governance",
      title: "Governance",
      paragraphs: [
        "Stage I — Foundation Governance: protocol development, smart contract deployment, treasury administration, exchange relationships, security oversight, regulatory engagement and partnership development.",
        "Stage II — Community Participation: JCV holders begin participating through formal proposals, potentially covering ecosystem grants, marketing initiatives, community events, educational programs and feature prioritization.",
        "Stage III — DAO Governance (future phase): governance authority progressively transitions to a decentralized autonomous organization with potential responsibilities including treasury allocations, protocol upgrades, grant approvals, strategic partnerships and ecosystem expansion.",
      ],
    },
    {
      id: "security",
      title: "Security",
      paragraphs: [
        "The Jovino approach to security: minimize unnecessary smart-contract complexity, reduce privileged administrative functions, commission independent security audits before launch, maintain responsible vulnerability disclosure, protect the treasury with multi-signature controls, secure private keys with hardware, and maintain operational monitoring and incident response planning.",
        "Independent security audit — planned before deployment. This document does not claim a completed audit.",
      ],
    },
    {
      id: "roadmap",
      title: "Roadmap",
      list: [
        "Phase I — Foundation (Year 1): white paper, foundation, smart contract deployment, independent security audits, official website, developer documentation, community channels",
        "Phase II — Market Launch (Year 1–2): launch JCV, initial liquidity, decentralized exchange listings, exchange outreach, Jovino Coin Wallet, staking",
        "Phase III — Ecosystem Growth (Year 2–3): governance portal, merchant payment integrations, developer SDK, ecosystem grants, community-built applications, international awareness",
        "Phase IV — Decentralization (Year 3–4): DAO governance, treasury oversight transition, ecosystem partnerships, interoperability, developer tooling",
        "Phase V — Global Expansion (Year 5+): mature governance, enterprise adoption, merchant network expansion, cross-chain integrations, continued innovation",
      ],
      paragraphs: [
        "Roadmap items represent strategic objectives rather than guaranteed delivery dates. Progress is subject to technical readiness, security considerations, available resources and community priorities.",
      ],
    },
    {
      id: "interoperability",
      title: "Interoperability (Future Development)",
      paragraphs: [
        "JCV launches on BNB Smart Chain while maintaining an architecture designed with future interoperability in mind. Potential future directions include cross-chain bridges, wrapped assets, decentralized interoperability protocols and multi-chain application support.",
      ],
    },
    {
      id: "presale",
      title: "Presale",
      paragraphs: [
        "The JCV presale begins 1 November 2026. Detailed presale mechanics, pricing, allocation, participation requirements and purchase information will be announced in a future version of the Jovino Coin documentation (White Paper Version 2.0 — Presale & Launch Economics).",
      ],
    },
    {
      id: "risk",
      title: "Risk Disclosure",
      list: [
        "JCV is designed as a decentralized digital utility token.",
        "Ownership of JCV does not represent equity, debt, ownership, profit-sharing rights or a claim against the Jovino Coin Foundation or affiliated organizations.",
        "Digital assets involve significant risks. Cryptocurrency markets can be highly volatile.",
        "Smart-contract vulnerabilities may occur and cybersecurity threats exist.",
        "Regulatory requirements may change and technological failures can occur.",
        "Digital assets can be lost.",
        "This document does not constitute financial, legal, tax, accounting, investment or regulatory advice.",
        "No future performance or financial return is guaranteed.",
        "Readers should conduct independent research and seek qualified professional advice.",
      ],
    },
  ],
};
